import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { updateQuickActions } from '@/services/users';
import { useAuthStore } from './authStore';

interface QuickActionsState {
	quickActions: Record<string, string>; // { [href]: permission }
}

interface QuickActionsActions {
	setQuickActions: (actions: Record<string, string>) => void;
	addQuickAction: (href: string, permission: string) => Promise<void>;
	removeQuickAction: (href: string) => Promise<void>;
	toggleQuickAction: (href: string, permission: string) => Promise<void>;
	isStarred: (href: string) => boolean;
	clearQuickActions: () => void;
}

const initialState: QuickActionsState = {
	quickActions: {},
};

export const useQuickActionsStore = create<
	QuickActionsState & QuickActionsActions
>()(
	devtools(
		persist(
			(set, get) => ({
				...initialState,

				/**
				 * Set quick actions from login response
				 */
				setQuickActions: (actions: Record<string, string>) => {
					set(
						{
							quickActions: actions || {},
						},
						false,
						'quickActions/setQuickActions',
					);
				},

			/**
			 * Add a quick action (href + permission) - updates both backend and store
			 */
			addQuickAction: async (href: string, permission: string) => {
				const { quickActions } = get();
				if (!quickActions[href]) {
					const newQuickActions = { ...quickActions, [href]: permission };

					// Update store immediately for optimistic UI
					set(
						{
							quickActions: newQuickActions,
						},
						false,
						'quickActions/addQuickAction',
					);

					// Update backend
					const user = useAuthStore.getState().user;
					if (user?.userInformation?._id) {
						try {
							await updateQuickActions(
								user.userInformation._id,
								newQuickActions,
							);
							// Update auth store with new quickActions
							useAuthStore.getState().updateUserInformation({
								quickActions: newQuickActions,
							});
						} catch (error) {
							// Revert on error
							set(
								{
									quickActions: quickActions,
								},
								false,
								'quickActions/addQuickAction/error',
							);
							throw error;
						}
					}
				}
			},

			/**
			 * Remove a quick action (by href) - updates both backend and store
			 */
			removeQuickAction: async (href: string) => {
				const { quickActions } = get();
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { [href]: _, ...newQuickActions } = quickActions;

				// Update store immediately for optimistic UI
				set(
					{
						quickActions: newQuickActions,
					},
					false,
					'quickActions/removeQuickAction',
				);

				// Update backend
				const user = useAuthStore.getState().user;
				if (user?.userInformation?._id) {
					try {
						await updateQuickActions(
							user.userInformation._id,
							newQuickActions,
						);
						// Update auth store with new quickActions
						useAuthStore.getState().updateUserInformation({
							quickActions: newQuickActions,
						});
					} catch (error) {
						// Revert on error
						set(
							{
								quickActions: quickActions,
							},
							false,
							'quickActions/removeQuickAction/error',
						);
						throw error;
					}
				}
			},

			/**
			 * Toggle a quick action (by href) - adds if not present, removes if present
			 */
			toggleQuickAction: async (href: string, permission: string) => {
				const { isStarred } = get();
				if (isStarred(href)) {
					await get().removeQuickAction(href);
				} else {
					await get().addQuickAction(href, permission);
				}
			},

			/**
			 * Check if an href is starred (in quick actions)
			 */
			isStarred: (href: string) => {
				const { quickActions } = get();
				return !!quickActions[href];
			},

			/**
			 * Clear all quick actions
			 */
			clearQuickActions: () => {
				set(
					{
						quickActions: {},
					},
					false,
					'quickActions/clearQuickActions',
				);
			},
			}),
			{
				name: 'quick-actions-store',
				partialize: (state) => ({ quickActions: state.quickActions }),
			},
		),
		{ name: 'QuickActionsStore' },
	),
);
